import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { map, findIndex, get, find } from 'lodash';
import NotificationSystem from 'react-notification-system';

import Select from 'components/Form/Select';
import Header from 'components/Header';
import Input from 'components/Form/Input';
import LargeDataTable from 'components/Tables/LargeDataTable';
import Card from 'components/Card';
import { Grid, Column } from 'components/Layout';
import { Button } from 'components/Button';

import { getApplications } from 'actions/application';
import { appConfig } from 'config/appConfig';
import { formatCurrency } from 'utils/formatCurrency';

import style from './style.scss';

const initialFilter = {
  applicationId: '',
  applicantName: '',
  applicationDate: '',
  statusLabel: '',
  statusAlias: '',
  settlementStatus: '',
  statusName: '',
  fundedAmount: '',
  purpose: '',
  merchant: '',
  assignedId: '',
  query: '',
  offset: 0,
  limit: appConfig.limit,
};

const query = {
  sortBy: 'applicationDate',
  sortOrder: 'desc',
};

const PreSelectionReview = (props) => {
  const dispatch = useDispatch();
  const columns = [
    { name: 'applicationId', title: 'Applicant ID', type: 'text', valueType: 'number' },
    { name: 'clientId', title: 'Client ID', type: 'text' },
    { name: 'applicantName', title: 'Name', type: 'text', valueType: 'text' },
    { name: 'statusAlias', title: 'Status', type: 'status', width: 180, valueType: 'text' },
    { name: 'totalLoanAmount', title: 'Pre-selected Amt', type: 'text', width: 180, valueType: 'number' },
    { name: 'monthlySavings', title: 'Committed Savings Amt', type: 'text', width: 200, valueType: 'number' },
  ];
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(1);
  const [rows, setRows] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [initialLoadFinished, setInitialLoadFinished] = useState(false);
  const [searchValue, setSearchValue] = useState(props.match.params.searchQuery);
  const [filterObj, setFilterObj] = useState({
    applicationId: '',
    applicantName: '',
    applicationDate: '',
    statusLabel: '',
    statusName: '',
    fundedAmount: '',
    purpose: '',
    merchant: '',
    assignedId: '',
    query: '',
    offset: 0,
    limit: appConfig.limit,
  });
  const notificationSystem = useRef(null);
  const filters = JSON.parse(localStorage.getItem('filters'));

  const getParameter = (selectedValueArg, filterObjArg, searchValueArg, queryArg = {}) => {
    const selectedQuery = get(selectedValueArg, 'attributes.query');
    const newQuery = { ...selectedQuery, ...queryArg };
    delete newQuery.applicationDateOffsetEnd;
    const urlEncode = (obj) => {
      Object.keys(obj).forEach((key) => {
        const str = (typeof obj[key] === 'object') ? JSON.stringify(obj[key]) : obj[key];
        // eslint-disable-next-line no-param-reassign
        obj[key] = encodeURIComponent(str);
      });
      return obj;
    };
    const ret = {
      filterObjArg,
      search: searchValueArg,
      queryArg: urlEncode(newQuery),
    };
    return ret;
  };

  const getApplicationsData = ({
    filterObjArg = initialFilter,
    search = '',
    queryArg = { sortBy: 'applicationDate', sortOrder: 'desc' },
  }) => {
    const filtersTemp = [];

    filtersTemp[0] = (filterObjArg.applicationId !== '') ? `applicationId=${filterObjArg.applicationId}&` : '';
    filtersTemp[1] = (filterObjArg.clientId !== '') ? `channelAttributesClientId=${filterObjArg.clientId}&` : '';
    filtersTemp[2] = (filterObjArg.applicantName !== '') ? `applicantName=${filterObjArg.applicantName}&` : '';
    filtersTemp[3] = (filterObjArg.statusLabel !== '') ? `statusLabel=${filterObjArg.statusLabel}&` : '';
    filtersTemp[4] = filterObjArg.monthlySavings ? `preSelection.monthlySavings=${filterObjArg.monthlySavings}&` : '';
    filtersTemp[5] = filterObjArg.totalLoanAmount ? `preSelection.totalLoanProceeds=${filterObjArg.totalLoanAmount}&` : '';
    filtersTemp[6] = localStorage.getItem('merchantId') ? `merchantId=${localStorage.getItem('merchantId')}&` : `merchantId=${null}&`;
    filtersTemp[7] = queryArg !== '' ? `${map(queryArg, (value, key) => `${key}=${value}`).join('&')}&` : '';
    let terms = '';
    if (search !== '') { terms = `Terms=${search}&`; }

    const documentTags = props.match.params.documentTags || null;
    if (documentTags) { filtersTemp[8] = `DocumentTags=${documentTags}&`; }

    const url =
      `/applications-view/search?${
        filtersTemp.join('')
      }&${
        terms
      }offset=${
        filterObjArg.offset
      }&limit=${
        filterObjArg.limit
      }`;
    setLoading(true);
    dispatch(getApplications({
      url,
      success: (res) => {
        const newRows = res.data.map((item) => {
          const {
            applicationId,
            applicantName,
            statusAliases,
            statusLabel,
            statusCode,
            channelAttributes,
            preSelection,
          } = item;

          return {
            applicationId,
            clientId: channelAttributes ? channelAttributes.clientId || '' : '',
            applicantName,
            statusLabel,
            statusCode,
            statusAlias: statusAliases ? statusAliases.merchantPortal : '',
            monthlySavings:
              parseFloat(get(preSelection, 'monthlySavings', '0')) > 0
                ? `$ ${formatCurrency(get(preSelection, 'monthlySavings'), 2)}`
                : '-',
            totalLoanAmount:
              parseFloat(get(preSelection, 'totalLoanProceeds', '0')) > 0
                ? `$ ${formatCurrency(get(preSelection, 'totalLoanProceeds'), 2)}`
                : '-',
          };
        });

        setRows(filterObjArg.offset > 0 ? [...rows, ...newRows] : newRows);
        setRowCount(res.pagination.total);
        setLoading(false);
      },
      fail: (err) => {
        setLoading(false);

        if (err.data) {
          notificationSystem.current
          && notificationSystem.current.addNotification({
            message: err.data.message,
            level: 'error',
            position: 'tc',
          });
        }
      },
    }));
  };

  const handleSearchClick = (e) => {
    e.preventDefault();
    const filterObjTemp = {
      ...filterObj,
      offset: 0,
    };

    setFilterObj(filterObjTemp);
    const param = getParameter(JSON.parse(selectedValue || '{}'), filterObjTemp, searchValue, null, { sortBy: 'applicationDate', sortOrder: 'desc' });
    getApplicationsData(param);
  };

  const handleKeydown = (event) => {
    if (event.keyCode === 13 && event.target.name === 'searchInput') {
      handleSearchClick(event);
    }
  };

  useEffect(() => {
    const { statusCode } = props.match.params;
    const selectedValueIndex = findIndex(
      get(find(
        filters,
        { name: 'PreSelection' }
      ), 'filters', []),
      item => item.name === statusCode
    );
    setSelectedValue(JSON.stringify(get(
      get(
        find(
          filters,
          { name: 'PreSelection' }
        ),
        'filters',
        []
      ),
      `${selectedValueIndex}`
    )));
    setInitialLoadFinished(true);
  }, []);

  const filterHandler = (queries) => {
    const filterObjTemp = {
      applicationId: queries.keys[0].data,
      clientId: queries.keys[1].data,
      applicantName: queries.keys[2].data,
      statusLabel: queries.keys[3].data,
      totalLoanAmount: queries.keys[4].data,
      monthlySavings: queries.keys[5].data,
      offset: queries.pageOffset,
      limit: queries.pageLength,
    };
    const { sorting } = queries;
    query.sortBy = sorting.columnName;
    query.sortOrder = sorting.direction;

    const param = getParameter(JSON.parse(selectedValue || '{}'), filterObjTemp, searchValue, null, query);
    getApplicationsData(param);
    setFilterObj(filterObjTemp);
  };

  const handleFilterChange = (e) => {
    const newSelectedValue = e.target.value;
    const filterObjTemp = {
      ...filterObj,
      offset: 0,
    };

    setSelectedValue(newSelectedValue);
    setFilterObj(filterObjTemp);
    const param = getParameter(JSON.parse(newSelectedValue || '{}'), filterObjTemp, searchValue, null, { sortBy: 'applicationDate', sortOrder: 'desc' });
    getApplicationsData(param);
  };

  const handleChange = (e) => {
    e.preventDefault();
    setSearchValue(e.target.value);
  };

  return initialLoadFinished && (
    <Fragment>
      <Header />
      <NotificationSystem ref={notificationSystem} />
      <section className="container section">
        <div className="grid-container fluid portal page-applications">
          <Grid>
            <Column className="small-12 card-grid-container">

              <Grid className={cn('grid-margin-x page-header justify-content-flex-end', style.noTopMargin)}>
                <Column className="cell small-12">
                  <h3>Pre-Selection Review</h3>
                </Column>
                <Column className="cell small-12 large-3">
                  <Select
                    name="form-field-name"
                    value={selectedValue}
                    onChange={handleFilterChange}
                    placeHolder="All Applications"
                    data={get(find(
                      filters,
                      { name: 'PreSelection' }
                    ), 'filters', []).map(item => ({
                      value: JSON.stringify(item),
                      title: item.attributes.alias ? item.attributes.alias : item.label,
                    }))}
                    hasDefault={false}
                    isErrorVisible={false}
                  />
                </Column>
                <Column className="cell small-12 large-3 search-input">
                  <Input
                    onChange={handleChange}
                    placeHolder="Search"
                    value={searchValue}
                    name="searchInput"
                    onKeyDown={handleKeydown}
                  />
                  <Button
                    className="button green arrow"
                    onClick={handleSearchClick}
                  />
                </Column>
              </Grid>

              <Card title="All Applications">
                <LargeDataTable
                  key="filterTable_default"
                  className="grid-x card-table"
                  columns={columns}
                  defaultSorting={[{ columnName: query.sortBy, direction: query.sortOrder }]}
                  pageOffset={{ offset: filterObj.offset, limit: filterObj.limit }}
                  hasGrouping
                  hasSorting
                  loading={loading}
                  rows={rows}
                  rowCount={rowCount}
                  isPreSelection
                  onFilter={(queries, isInitial, isHeaderTag) => { filterHandler(queries, isInitial, isHeaderTag); }}
                  infiniteScroll
                />
              </Card>
            </Column>
          </Grid>
        </div>
      </section>
    </Fragment>
  );
};

PreSelectionReview.propTypes = {
  match: PropTypes.object.isRequired,
};

export default withRouter(PreSelectionReview);



// WEBPACK FOOTER //
// ./src/containers/pages/ApplicationReview/PreSelectionReview.js